export default function createStore(reducer, preloadedState, enhancer) {

  if (typeof enhancer === 'function') {
    return enhancer(createStore)(reducer, preloadedState)
  }

  const listeners = [];
  let currentReducer = reducer;
  let currentState = preloadedState;
  let isDispatching = false;

  const callDebouncedListeners = debounce(callListeners, 50);

  return {
    dispatch,
    subscribe,
    getState,
    get
  };

  function getState() {
    if (isDispatching) {
      throw new Error(
        'You may not call store.getState() while the reducer is executing. ' +
        'The reducer has already received the state as an argument. ' +
        'Pass it down from the top reducer instead of reading it from the store.'
      )
    }

    return currentState;
  }

  function get(key) {
    return getState()[key];
  }

  function subscribe(listener) {
    if (typeof listener !== 'function') {
      throw new Error('Expected the listener to be a function.')
    }

    if (isDispatching) {
      throw new Error(
        'You may not call store.subscribe() while the reducer is executing. ' +
        'If you would like to be notified after the store has been updated, subscribe from a ' +
        'component and invoke store.getState() in the callback to access the latest state. ' +
        'See https://redux.js.org/api-reference/store#subscribe(listener) for more details.'
      )
    }

    listeners.push(listener);

    listener(currentState);
  }

  function dispatch(action) {
    if (typeof action.type === 'undefined') {
      throw new Error(
        'Actions may not have an undefined "type" property. ' +
        'Have you misspelled a constant?'
      )
    }

    if (isDispatching) {
      throw new Error('Reducers may not dispatch actions.')
    }

    try {
      isDispatching = true
      currentState = currentReducer(currentState, action)
    } finally {
      isDispatching = false
    }

    callDebouncedListeners();

    return action
  }

  function callListeners() {
    for (let i = 0; i < listeners.length; i++) {
      const listener = listeners[i];
      listener(currentState)
    }
  }

  // https://davidwalsh.name/function-debounce
  // removed immediate option
  function debounce(func, wait) {
    var timeout;
    return function () {
      var context = this, args = arguments;
      var later = function () {
        timeout = null;
        func.apply(context, args);
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}
